import React, {
	ChangeEvent,
	useState,
	useReducer,
	FormEvent,
	useEffect,
} from 'react';
import { text, unitList } from '../data';
import PartSearch from '../../components/bearing-search/components/PartSearch';
import {
	BearingCalculationData,
	BearingInfo,
	BearingPeriodicFrequenciesPayload,
	TimkenUnit,
} from '../../components/bearing-search/types/types';
import { getPeriodicFrequencies } from '../../components/bearing-search/api/api';
import CalculationResults from '../components/CalculationResults';
import periodicFrequenciesReducer, { initialFrequenciesState } from './reducer';
import partReducer, {
	BearingPartState,
	initialBearingPartState,
} from '../../components/bearing-search/reducers/reducer';
import {
	getDimensionLabel,
	toggleImportantNote,
	toggleNextTools,
	updateToolParams,
	getPartNumberFromUrl,
	getPartStateFromParams,
	toggleReportReady,
} from '../utils/engineering-tools';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import NumberInput from '../../components/bearing-search/components/forms/NumberInput';
import SelectDropdown from '../../components/bearing-search/components/forms/SelectDropdown';
import {
	trackStart,
	trackResults,
	updateActionParams,
} from '../utils/analytics';
import PartPreSearch from '../components/PartPreSearch';

const PeriodicFrequencies: React.FC = () => {
	const [ isPartSelected, setIsPartSelected ] = useState<boolean>(null);
	const [ isPartOpen, setIsPartOpen ] = useState( false );
	const [ isCalculated, setIsCalculated ] = useState( false );
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ searchInput, setSearchInput ] = useState( '' );
	const [ results, setResults ] = useState<Array<BearingCalculationData | null>>(
		[],
	);
	const [ comments, setComments ] = useState( '' );
	const [ graph, setGraph ] = useState(null);
	const [ partState, partDispatch ] = useReducer(
		partReducer,
		initialBearingPartState,
	);
	const [ dimensionLabel, setDimensionLabel ] = useState( '' );
	const [ formState, dispatch ] = useReducer(
		periodicFrequenciesReducer,
		initialFrequenciesState,
	);

	useEffect( () => {
		trackStart( 'bearingPeriodicFrequencies' );

		const queryMap = new URLSearchParams( document.location.search );
		const savedPart = getPartStateFromParams( queryMap );

		if (queryMap.size == 1) {
			const partNumberFromUrl = getPartNumberFromUrl();
			if (partNumberFromUrl) {
				setSearchInput(partNumberFromUrl);
				partDispatch({
					type: 'select_part',
					payload: { marketPlaceDescription: partNumberFromUrl },
				});
				setIsPartSelected( true );
			}
		} else if (queryMap.size > 1) {
			if ( savedPart.partNumber ) {
				partDispatch( {
					type: 'reset',
					payload: savedPart,
				} );
				setIsPartSelected( true );
				setSearchInput( savedPart.marketPlaceDescription || '' );
			}
		}
	}, [] );

	// Show / hide recommended tools
	useEffect( () => {
		const isShown = isCalculated && ! isResultsLoading;
		toggleNextTools( isShown );
		toggleReportReady(
			isShown,
			partState.marketPlaceDescription,
			partState.primaryImage,
		);

		return () => toggleNextTools( false );
	}, [ isCalculated, isResultsLoading ] );

	// Pass partNumber and bearingClass to recommended tools link
	useEffect( () => {
		updateToolParams<BearingPartState>( partState );

		return () => {
			updateToolParams();
		};
	}, [ partState.marketPlaceDescription ] );

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		const value = event.target.value;
		setSearchInput( value );
		setIsPartSelected( false );
	};

	const verifyPart = ( part?: BearingInfo ) => {
		if ( part ) {
			partDispatch( {
				type: 'select_part',
				payload: part,
			} );
			setIsPartSelected( true );
			setSearchInput( part.marketPlaceDescription || '' );
		}

		setIsPartOpen( false );
	};

	const clearSearch = () => {
		setSearchInput( '' );
		setIsCalculated( false );
		setIsPartSelected( false );
		toggleImportantNote( false );

		partDispatch( {
			type: 'reset',
		} );

		dispatch( {
			type: 'reset',
		} );
	};

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();

		if ( ! partState.partNumber ) {
			// Search Part
			setIsPartOpen( true );
		} else {
			// Calculate Periodic Frequencies
			setIsCalculated( true );
			setIsResultsLoading( true );
			toggleImportantNote( false );

			const { partNumber, cupPartNumber, bearingType, bearingSubType } =
				partState;

			const request: Partial<BearingPeriodicFrequenciesPayload> = {
				partNumberOrWorldPartNumber: partNumber,
				cupPartNumber,
				bearingType,
				bearingSubType,
				...formState,
			};

			getPeriodicFrequencies( request )
				.then( ( response ) => {
					updateActionParams( 'bearingPeriodicFrequencies', partState );

					let reportData: Array<BearingCalculationData | null> = [];
					let reportComment = '';
					let graph = null;

					if ( response.report && response.report.length > 0 ) {
						reportData = response.report[ 0 ].data;
						reportComment = response.report[ 0 ].comments || '';
					}

					if (response.periodicFrequencyResult && response.periodicFrequencyResult.length > 0) {
						graph = `data:image/png;base64,${response.periodicFrequencyResult[ 0 ].chart}`;
					}

					setResults( reportData );
					trackResults( 'bearingPeriodicFrequencies', request, reportData.length, partNumber );
					setDimensionLabel( getDimensionLabel( partState, formState.units ) );
					setComments( reportComment );
					toggleImportantNote( true );
					setGraph(graph);

					document.querySelector('.t-engineering-tools__results').scrollIntoView({
						behavior: 'smooth',
					});
				} )
				.finally( () => setIsResultsLoading( false ) );
		}
	};

	const preSearchClickHandler = () => {
		setIsPartSelected(true);
	};

	const setPartFromPreSearchAndSubmit = (value: string, e: React.MouseEvent<HTMLElement>) => {
		setSearchInput( value );
		handleSubmit(e);
	};

	return (
		<>
			<div>
				<form onSubmit={handleSubmit}>
					<div className="t-engineering-tools__form-fields t-engineering-tools__form-fields--multi-column timken-tool-presearch-container">
						<TextInput
							id="periodic_frequencies_part_number"
							required={true}
							label={text.partNumber}
							verified={!! partState.partNumber}
							name="partNumber"
							value={searchInput}
							disabled={!! partState.partNumber}
							placeholder={text.partNumber}
							message={
								partState.partNumber
									? `${ text.bearingType }: ${ partState.bearingTypeLabel }`
									: text.partWildCardHint
							}
							onChange={inputPartNumber}
						/>

						<PartPreSearch
							input={searchInput}
							isHidden={searchInput.length < 2 || isPartOpen || isPartSelected}
							unit={formState.units}
							onClick={preSearchClickHandler}
							verifyPartNumber={(value: string, e) => setPartFromPreSearchAndSubmit(value, e)}
						/>

						<SelectDropdown
							id="bearing_tolerance_unit"
							name="unit"
							required={true}
							label={text.displayUnits}
							value={formState.units}
							options={unitList}
							onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
								dispatch( {
									type: 'change_unit',
									payload: event.target.value as TimkenUnit,
								} )
							}
						/>

						{! partState.partNumber && (
							<button
								className="btn btn--primary"
								type="submit"
								disabled={! searchInput}
							>
								{text.lookup}
							</button>
						)}

						{!! partState.partNumber && (
							<>
								<NumberInput
									id="periodic_frequencies_speed"
									name="operatingSpeed"
									required={true}
									label={text.operatingSpeed}
									value={formState.operatingSpeed}
									min={0}
									max={99999}
									onChange={( event: ChangeEvent<HTMLInputElement> ) =>
										dispatch( {
											type: 'change_operating_speed',
											payload: event.target.value,
										} )
									}
								/>
							</>
						)}
					</div>

					{!! partState.partNumber && (
						<div>
							<div className="t-engineering-tools__terms-of-use"
								  dangerouslySetInnerHTML={{ __html: text.calculateTermsOfUse }}
							/>
							<div className="t-engineering-tools__search-actions">
								<button
									className="btn btn--primary"
									type="submit"
									disabled={! formState.operatingSpeed}
								>
									{text.calculate}
								</button>

								<button
									className="btn btn--secondary"
									type="button"
									onClick={() => clearSearch()}
								>
									{text.clear}
								</button>
							</div>
						</div>
					)}
				</form>
			</div>

			{isCalculated && (
				<CalculationResults
					data={results}
					comments={comments}
					label={dimensionLabel}
					loading={isResultsLoading}
					graph={graph}
				/>
			)}

			{isPartOpen && !! searchInput && (
				<PartSearch
					units={formState.units}
					partNumber={searchInput}
					onSelect={verifyPart}
					onClose={() => setIsPartOpen( false )}
				/>
			)}
		</>
	);
};

export default PeriodicFrequencies;
